import type { Locale } from '@autho/i18n';
import { formatRelative, isLocale } from '@autho/i18n';
import { CircleAlert, CircleCheck, CircleDashed, CircleSlash } from 'lucide-react';
import { useIntl } from 'react-intl';
import { cn } from './cn.ts';

export type ConnectionStatus = 'pending' | 'live' | 'error' | 'revoked';

const ICON = {
  pending: CircleDashed,
  live: CircleCheck,
  error: CircleAlert,
  revoked: CircleSlash,
} as const;
const TONE = {
  pending: 'text-muted',
  live: 'text-ok',
  error: 'text-danger',
  revoked: 'text-muted',
} as const;

/** Sync honesty (Non-Functional.md): last success, last attempt and state, always in words. */
export function SyncStatus({
  status,
  lastSuccessAt,
  lastAttemptAt,
  lastError,
  compact = false,
}: {
  status: ConnectionStatus;
  lastSuccessAt: string | null;
  lastAttemptAt: string | null;
  lastError?: string | null;
  compact?: boolean;
}) {
  const intl = useIntl();
  const locale = (isLocale(intl.locale) ? intl.locale : 'en') as Locale;
  const Icon = ICON[status];
  const ago = (iso: string | null) =>
    iso ? formatRelative(iso, locale) : intl.formatMessage({ id: 'configure.never' });
  return (
    <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
      <span className={cn('inline-flex items-center gap-1 font-medium', TONE[status])}>
        <Icon aria-hidden className="size-3.5" />
        {intl.formatMessage({ id: `configure.status.${status}` })}
      </span>
      <span className="text-muted">
        {intl.formatMessage({ id: 'configure.lastSuccess' }, { ago: ago(lastSuccessAt) })}
      </span>
      {!compact && (
        <span className="text-muted">
          {intl.formatMessage({ id: 'configure.lastAttempt' }, { ago: ago(lastAttemptAt) })}
        </span>
      )}
      {!compact && lastError && <span className="text-danger">{lastError}</span>}
    </span>
  );
}
